import { setToken } from '@library/token'
import { hasKeysOfUnknown, isObject } from 'common/helpers'
import type { Nullable } from 'common/types'
import Keycloak from 'keycloak-js'
import { useEffect, useRef } from 'react'

import type { AuthState, CurrentUser, KeycloakConfig } from './types'

const TOKEN_REFRESH_RATE = 5

type UseKeycloakConfigType = (
  keycloakConfig: Nullable<KeycloakConfig & { code: string }>,
  handleUserSet: (user: AuthState) => void,
) => void

declare global {
  interface XMLHttpRequest {
    originOpen: XMLHttpRequest['open']
  }
}

export const useKeycloakConfig: UseKeycloakConfigType = (keycloakConfig, handleUserSet) => {
  const keycloak = useRef<Nullable<Keycloak>>(null)

  useEffect(() => {
    if (!keycloakConfig) return

    // eslint-disable-next-line @typescript-eslint/unbound-method
    XMLHttpRequest.prototype.originOpen = XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.open = function <
      T extends Parameters<(typeof XMLHttpRequest)['prototype']['open']>,
    >(...args: T) {
      // eslint-disable-next-line prefer-spread
      this.originOpen.apply(this, args)
      if (args[1].toString().endsWith('token')) {
        this.setRequestHeader('Authorization', `Basic ${keycloakConfig.code}`)
      }
    }

    keycloak.current = new Keycloak(keycloakConfig)
    keycloak.current
      .init({ onLoad: 'login-required', checkLoginIframe: false })
      .then(authenticated => {
        if (!keycloak.current) return

        const loginUrl = keycloak.current.createLoginUrl()
        const logoutUrl = keycloak.current.createLogoutUrl()

        // eslint-disable-next-line @typescript-eslint/unbound-method
        const login = keycloak.current.login
        const logout = keycloak.current.logout.bind(null, {
          redirectUri: `${window.location.origin}/logout`,
        })

        const tokenParsed = keycloak.current.tokenParsed ?? null
        const token = keycloak.current.token ?? null

        keycloak.current.loadUserInfo().then((userInfo: unknown) => {
          setToken(token)
          if (token && isCurrentUser(userInfo)) {
            handleUserSet({
              authenticated,
              token,
              tokenParsed,
              loginUrl,
              logoutUrl,
              login,
              logout,
              userInfo,
            })
          }
        })
      })

    const handleOnExpired = () => {
      if (!keycloak.current) return

      keycloak.current.updateToken(TOKEN_REFRESH_RATE).then(refreshed => {
        const token = keycloak.current?.token ?? null
        if (refreshed) {
          setToken(token)
        }
      })
    }

    keycloak.current.onTokenExpired = handleOnExpired
  }, [handleUserSet, keycloakConfig])
}

function isCurrentUser(user: unknown): user is CurrentUser {
  const result = isObject(user) && hasKeysOfUnknown(user, 'sub')
  if (!result) {
    console.warn(`Объект ${JSON.stringify(user)} не соответсвует типу CurrentUser`)
  }
  return result
}
